import { getDecodedId } from './ProductUtils';
import { format } from 'date-fns';
import { Base64 } from 'js-base64';

/**
 * Get Encoded Variant ID
 *
 * DY gives us the Variant ID string => '1669710577684'
 * We need to encode the variant id to base64
 * so we can add the variant to cart
 *
 * @param {string} id - DY id (string of numbers)
 * @return {string} Encoded ID - returns encoded ID
 */
export var getEncodedVariantId = function getEncodedVariantId(id) {
  var formattedId = 'gid://shopify/ProductVariant/' + id;
  return Base64.encode(formattedId);
};

/**
 * Get Selected Variant
 *
 * If custom collection, return custom collection variant
 * else return first available variant
 *
 * @param {Object} variants - all product variants
 * @return {Object} return fist available variant or custom collection variant
 */
export var getInitialSelectedVariant = function getInitialSelectedVariant(variants) {
  var selectedVariantFromParameter = variants.find(function (v) {
    return getDecodedId(v.node.id) == parseInt(window.location.search.replace('?variant=', ''));
  });

  var customCollectionSelectedVariant = variants.find(function (v) {
    return v.node.default_variant == true;
  });
  var firstAvailable = variants.find(function (v) {
    return v.node.availableForSale == true;
  });
  return selectedVariantFromParameter ? selectedVariantFromParameter.node : customCollectionSelectedVariant ? customCollectionSelectedVariant.node : firstAvailable ? firstAvailable.node : variants[0].node;
};

/**
 * Get Selected Variant
 *
 * @param {Object} variants - all product variants
 * @param {Object} selectedOptions - currently selected options
 * @return {Object} selectedVariant - returns the filtered variant based on selectedOptions
 */
export var getSelectedVariant = function getSelectedVariant(variants, selectedOptions) {
  var selectedVariant = variants.find(function (variant) {
    return variant.node.selectedOptions.every(function (selectedOption) {
      return selectedOptions[selectedOption.name] === selectedOption.value;
    });
  });
  if (typeof selectedVariant != 'undefined') {
    return selectedVariant.node;
  }
};

/**
 * Check if Variant Available
 *
 * 1. filter variants to get variant with radios value
 * 2. if multiple selected options, filter based on other currently selected options
 * 3. if selected variant exists, return variant, else return false
 * @param {String} value - radio value ie `blue`
 * @param {String} name - radio option name ie `color`
 * @param {Object} selectedOptions - products currently selected options
 * @param {Array} variants - Array of product variants
 * @return {Object} radioVariant - returns sorted product array
 */
export var checkIfVariantAvailable = function checkIfVariantAvailable(value, name, selectedOptions, variants) {
  var currentOptionVariants = variants.filter(function (variant) {
    return variant.node.selectedOptions.some(function (option) {
      return option.value == value;
    });
  });
  var selectedVariant = Object.keys(selectedOptions).length > 1 ? getRadioVariantIfMultipleOptions(name, selectedOptions, currentOptionVariants) : currentOptionVariants;
  return selectedVariant.length ? selectedVariant[0].node : false;
};

/**
 * Get Radio Variant if multiple options (getRadioVariant helper function)
 *
 *  If multiple options,
 *  we need to filter the currentRadioValueVariants
 *  based on the selected options
 *
 * @param {String} name - radio option name ie `color`
 * @param {Object} selectedOptions - products currently selected options
 * @param {Array} currentRadioValueVariants - Array of product variants with current radio value
 * @return {Object} radioVariant - returns sorted product array
 */
export var getRadioVariantIfMultipleOptions = function getRadioVariantIfMultipleOptions(name, selectedOptions, currentRadioValueVariants) {
  var otherSelectedValue = selectedOptions[Object.keys(selectedOptions).filter(function (key) {
    return key != name;
  })];
  return currentRadioValueVariants.filter(function (variant) {
    return variant.node.selectedOptions.some(function (option) {
      return option.value == otherSelectedValue;
    });
  });
};

/**
 * Get initial Selected Options Object
 *
 * @param  {Array} options - array of options objects
 * @return  {Object} initialOptions - sets the first value of each option as initial value
 */
export var getInitialSelectedOptions = function getInitialSelectedOptions(selectedVariant) {
  var initialOptions = {};
  var options = selectedVariant.selectedOptions;
  options.forEach(function (option) {
    initialOptions[option.name] = option.value;
  });
  return initialOptions;
};

/**
 * Get New Selected Options Object
 *
 * @param {Object} prevSelectedOptions - Previous Selected Options object
 * @param {Object} newSelection - user changed event.target option
 * @return {Object} newSelectedOptions - returns updated prevSelectedOptions
 */
export var getNewSelectedOptions = function getNewSelectedOptions(prevSelectedOptions, newSelection) {
  // Added the product id to radio input name to allow focus of radio group
  // need to strip the product id from newSelectionName to get correct name attribute
  // example = color--Z2lkOi8vc2hvcGlmeS9Qcm9kdWN0LzkzMTc2NzY4MTEzOQ==
  var newSelectionName = newSelection.name.split('--')[0];
  prevSelectedOptions[newSelectionName] = newSelection.value;
  return prevSelectedOptions;
};

/**
 * Does Selected Variant Exist
 *
 * Loop through Selected Variant options
 *   if the selected variant options do not match selectedOptions
 *     variant doesn't exists
 *   else
 *     variant exists
 *
 * @param {Object} selectedOptions- selected options object
 * @param {Object} selectedVariant - selected Variant object
 * @return {boolean} variantExists
 */
export var doesSelectedVariantExist = function doesSelectedVariantExist(selectedOptions, selectedVariant) {
  var variantExists = true;
  if (selectedOptions === null || selectedVariant === null) {
    variantExists = false;
  } else {
    var selectedOptionValues = Object.values(selectedOptions);
    selectedVariant.selectedOptions.map(function (option) {
      if (!selectedOptionValues.includes(option.value)) {
        variantExists = false;
      }
    });
  }
  return variantExists;
};

/**
 * Get Pre Order Data
 *
 * if selected variant has preorder_date metafield data
 * create object with
 *    date:  js date object
 *    string: date as string
 *    isInFuture: bool if preorder date is in future
 *
 * @param {Object} props
 * @return {Object} preOrder Object
 */
export var getPreorderData = function getPreorderData(preorderDate) {
  var date = preorderDate && new Date(preorderDate.value.replace(/-/g, '/'));
  if (date) {
    return {
      date: date,
      string: format(date, 'MMMM d'),
      isInFuture: date > new Date()
    };
  }
};

/***
 * Render Default Message On Select
 *
 * Renders default message depending on option name
 */

export var renderDefaultMessageOnSelect = function renderDefaultMessageOnSelect(name) {
  return name === 'Base Pillow' ? 'Choose Pillow Color' : 'Choose Size';
};